import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import { IconButton } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { helpers } from '@turf/turf'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import AppContext from '../../context/appContext'
import deleteMetric from '../../services/ruutsApi/deleteMetric'
import { processLoading } from '../../utils/Loading/processLoading'
import { DCNamespaces } from '../../utils/dataCollection/namespaces'
import { queryKeys } from '../../hooks/data/queryKeys'
import { useFarmSubdivisions } from '../../hooks/data/useFarmSubdivisions'
import { useFormDefinitions } from '../../hooks/data/useFormDefinitions'
import { useMetricEvents } from '../../hooks/data/useMetricEvents'
import { usePaddocksByIds } from '../../hooks/data/usePaddocks'
import { useQUHints } from '../../hooks/data/useQUHints'
import DataCollectionMap from '../DataCollectionMap/DataCollectionMap'
import ExcessHectaresWarning from '../DataCollectionMap/ExcessHectaresWarning'
import PaddocksList from '../DataCollectionMap/PaddocksList'
import { DataTable } from '../DataTable/DataTable'
import DataCollectionDynamicForm from '../DynamicForm/DataCollectionDynamicForm'
import { useDraftFormStorage } from '../DynamicForm/useDraftFormStorage'
import { ModalTypes } from '../GlobalConfirmationModal/GlobalConfirmationModal'

// Hoisted for referential stability: DataCollectionMap keys an effect on this prop.
const NO_PADDOCK_PRODUCTION_EVENTS = []

const FormContainer = ({
  formMode,
  selectedMetricEvent,
  setSelectedMetricEvent,
  dataCollectionStatementId,
  formKey,
  year,
  resetTab,
}) => {
  const {
    setConfirmationModalConfig,
    setLoadingModalConfig,
    userRoleSelected,
    currentFarm,
    programConfig,
  } = useContext(AppContext)
  const quConfigEnabled = !!programConfig?.quConfig?.enabled

  // Same cache entry DataCollectionForm reads (keyed by programConfig.id); React Query dedupes
  // the concurrent call, so this is a cache lookup, not a second request.
  const { data: formDefinitions } = useFormDefinitions(programConfig)
  const formDefinition = useMemo(
    () => formDefinitions?.find(fd => fd.namespace === formKey),
    [formDefinitions, formKey],
  )
  const { data: farmSubdivisions } = useFarmSubdivisions({
    farmId: currentFarm.id,
    year,
    enabled: !!year,
  })
  const { data: subdivisionPaddocks } = usePaddocksByIds({
    ids: farmSubdivisions?.[0]?.paddockIds,
  })

  const paddockIds = useMemo(
    () => (subdivisionPaddocks ?? []).map(paddock => paddock.id),
    [subdivisionPaddocks],
  )
  const paddocksGeoJson = useMemo(
    () => helpers.featureCollection((subdivisionPaddocks ?? []).map(paddock => paddock.toGeoJSON)),
    [subdivisionPaddocks],
  )
  const [selectedPaddockIds, setSelectedPaddockIds] = useState([])
  const [usedPaddockIds, setUsedPaddockIds] = useState([])
  const [allSelected, setAllSelected] = useState(false)
  const [drafts, setDrafts] = useState([])
  const [resetFormSignal, toggleResetFormSignal] = useState(false)
  const [disableMap, setDisableMap] = useState(false)
  const [showMetrics, setShowMetrics] = useState(false)

  const { getDrafts, removeFromDrafts } = useDraftFormStorage({
    namespace: formDefinition?.namespace,
    farmId: currentFarm.id,
    year,
  })

  const queryClient = useQueryClient()
  const {
    data: savedMetricEvents,
    isLoading: metricEventsLoading,
    isError: metricEventsFailed,
    error: metricEventsError,
    isSuccess: metricEventsLoaded,
  } = useMetricEvents({
    farmId: currentFarm.id,
    namespace: formDefinition?.namespace,
    year,
    enabled: !!formDefinition && !!year,
  })
  const { data: paddockProductionEvents } = useMetricEvents({
    farmId: currentFarm.id,
    namespace: DCNamespaces.paddocks,
    year,
    enabled: formDefinition?.namespace === DCNamespaces.grazing && !!year,
  })

  const metricEvents = useMemo(
    () => [...(savedMetricEvents ?? []), ...drafts],
    [savedMetricEvents, drafts],
  )

  // Drafts live in localStorage, outside React Query: nothing invalidates them, so they are
  // re-read on every reset the form signals and whenever the storage key changes.
  useEffect(() => {
    setDrafts(getDrafts())
  }, [getDrafts])

  const isPaddocksUsage = formDefinition?.namespace === DCNamespaces.paddocksUsage
  const { data: quHintsData } = useQUHints({
    farmId: currentFarm.id,
    year,
    enabled: isPaddocksUsage && quConfigEnabled && !!currentFarm?.id,
  })

  // The grazing form is the only one that also reads the paddock-production list, and that list
  // went stale with the main one under the single imperative fetch this replaced.
  const refreshMetricEvents = useCallback(() => {
    const farmId = currentFarm.id
    const namespace = formDefinition?.namespace
    const staleNamespaces =
      namespace === DCNamespaces.grazing ? [namespace, DCNamespaces.paddocks] : [namespace]

    staleNamespaces.forEach(staleNamespace =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.metricEvents({ farmId, namespace: staleNamespace, year }),
      }),
    )
    if (namespace === DCNamespaces.grazing) {
      queryClient.invalidateQueries({ queryKey: queryKeys.holisticPlanIssuesRoot(farmId) })
    }
  }, [queryClient, currentFarm.id, formDefinition?.namespace, year])

  const handleMetricReset = () => {
    setSelectedMetricEvent(null)
    setDrafts(getDrafts())
    refreshMetricEvents()
    resetTab()
  }

  // The loading modal is this screen's only loading affordance — there is no inline skeleton.
  useEffect(() => {
    if (metricEventsLoading) {
      setLoadingModalConfig({ open: true, dialogMessage: 'Cargando métricas' })
      return
    }
    if (metricEventsFailed) {
      setLoadingModalConfig({
        open: false,
        error: metricEventsError,
        errorMessage: 'Error al cargar las métricas, por favor intente nuevamente',
      })
      return
    }
    if (!metricEventsLoaded) return

    setSelectedMetricEvent(null)
    setLoadingModalConfig({ open: false })
    setShowMetrics(true)
  }, [
    metricEventsLoading,
    metricEventsFailed,
    metricEventsError,
    metricEventsLoaded,
    setLoadingModalConfig,
    setSelectedMetricEvent,
  ])

  async function handleDeleteMetric(metric, deletedMetricReason) {
    await processLoading({
      setLoadingModalConfig,
      loadingMessage: 'Borrando métrica...',
      successfulMessage: 'Datos eliminados correctamente',
      errorMessage: 'Error al eliminar la métrica, por favor intente nuevamente',
      doAction: async () => {
        await deleteMetric(
          metric.id,
          dataCollectionStatementId,
          deletedMetricReason,
          userRoleSelected,
        )
        handleMetricReset()
      },
    })
  }

  function handleSelectedMetric(row) {
    const selected = metricEvents.find(metric => metric.id === row[0].id)
    setSelectedMetricEvent({ ...selected, dataIndex: row[0].index + 1 })
  }

  function handleDeleteConfirmation(row) {
    const metricEventToDelete = metricEvents.find(metric => metric.id === row[0].id)

    if (metricEventToDelete.isDraft) {
      setConfirmationModalConfig({
        open: true,
        title: 'Borrar borrador',
        message: 'Se borrará el borrador seleccionado. ¿Desea continuar?',
        confirmLabel: 'Borrar',
        modalType: ModalTypes.DeleteModalType,
        confirmAction: () => {
          removeFromDrafts(metricEventToDelete.id)
          handleMetricReset()
        },
      })
    } else {
      const hasFindingToResolve = metricEventToDelete.Findings?.some(
        finding => finding.resolved === false,
      )
      let requiredTextField = null
      let confirmationMessage = 'Se borrará la métrica seleccionada. ¿Desea continuar?'
      if (hasFindingToResolve) {
        confirmationMessage =
          'La métrica seleccionada posee una observación sin resolver por el auditor. Para poder eliminarla es necesario que detalle el motivo ¿Desea continuar?'
        requiredTextField = {
          placeHolder: 'Motivo',
        }
      }
      setConfirmationModalConfig({
        open: true,
        title: 'Borrar métrica',
        message: confirmationMessage,
        confirmLabel: 'Borrar',
        modalType: ModalTypes.DeleteModalType,
        confirmAction: input => handleDeleteMetric(metricEventToDelete, input),
        requiredTextField,
      })
    }
  }

  function handleResetForm() {
    toggleResetFormSignal(!resetFormSignal)
    setSelectedMetricEvent(null)
  }

  return (
    formMode &&
    formDefinition &&
    paddocksGeoJson?.features?.length > 0 &&
    metricEvents && (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack flex direction="row" flexDirection="row" flexGrow={0} height="100%">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#eceff1',
              width: !formDefinition?.layout?.showMap
                ? '100%'
                : !disableMap
                  ? '50%'
                  : 'calc(100% - 30px)',
            }}
          >
            {isPaddocksUsage && quHintsData?.proposedMatches?.length > 0 && (
              <ExcessHectaresWarning
                paddocks={paddocksGeoJson?.features?.map(feature => ({
                  id: feature.properties.id,
                  name: feature.properties.name,
                }))}
                proposedMatches={quHintsData.proposedMatches}
                quantificationUnits={quHintsData.quantificationUnits}
              />
            )}
            <DataCollectionDynamicForm
              allowMoreRecords={formDefinition.allowMultipleRecords || !metricEvents?.length}
              dataCollectionStatementId={dataCollectionStatementId}
              formDefinition={formDefinition}
              formMode={formMode}
              handleDisableMap={setDisableMap}
              handleMetricReset={handleMetricReset}
              handleResetForm={() => handleResetForm()}
              selectedMetricEvent={selectedMetricEvent}
              selectedPaddockIds={selectedPaddockIds}
              year={year}
            >
              {formDefinition.layout.showMap && (
                <PaddocksList
                  allSelected={allSelected}
                  formMode={formMode}
                  paddocks={paddocksGeoJson.features.map(feature => {
                    return { id: feature.properties.id, label: feature.properties.name }
                  })}
                  selectedPaddockIds={selectedPaddockIds}
                  setAllSelected={setAllSelected}
                  setSelectedPaddockIds={setSelectedPaddockIds}
                  usedPaddockIds={usedPaddockIds}
                />
              )}
            </DataCollectionDynamicForm>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
            {formDefinition.layout.showMap && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                sx={{
                  height: 'calc(100% - 46px)',
                  width: '30px',
                  border: 'solid',
                  borderWidth: 1,
                  borderRadius: 1,
                  borderColor: '#bdbdbd',
                  boxSizing: 'border-box',
                  backgroundColor: '#eceff1',
                  display: 'flex',
                  padding: '4px',
                  alignItems: 'center',
                }}
                onClick={() => {
                  setDisableMap(v => !v)
                }}
              >
                {disableMap ? (
                  <KeyboardDoubleArrowLeft fontSize="inherit" />
                ) : (
                  <KeyboardDoubleArrowRight fontSize="inherit" />
                )}
              </IconButton>
            )}
            {formDefinition.layout.showMap && !disableMap && (
              <Box
                sx={{
                  flexBasis: '50%',
                  height: '100%',
                  backgroundColor: '#eceff1',
                }}
              >
                <DataCollectionMap
                  allPaddockIds={paddockIds}
                  allSelected={allSelected}
                  center={[-34.5, -58.4]}
                  formMode={formMode}
                  metricEvents={metricEvents}
                  namespace={formDefinition.namespace}
                  paddockProductionEvents={paddockProductionEvents ?? NO_PADDOCK_PRODUCTION_EVENTS}
                  paddocksGeoJson={paddocksGeoJson}
                  quantificationUnits={quHintsData?.quantificationUnits}
                  resetFormSignal={resetFormSignal}
                  selectedMetricEvent={selectedMetricEvent}
                  selectedPaddockIds={selectedPaddockIds}
                  setAllSelected={setAllSelected}
                  setSelectedPaddockIds={setSelectedPaddockIds}
                  setUsedPaddockIds={setUsedPaddockIds}
                  usedPaddockIds={usedPaddockIds}
                />
              </Box>
            )}
          </Box>
        </Stack>

        {showMetrics && (
          <DataTable
            farmSubdivision={paddocksGeoJson}
            formDefinition={formDefinition}
            formMode={formMode}
            handleDelete={row => handleDeleteConfirmation(row)}
            handleMetricReset={handleMetricReset}
            handleSelect={row => handleSelectedMetric(row)}
            metricEvents={metricEvents}
          />
        )}
      </Box>
    )
  )
}

export default FormContainer
